import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import AppShell from './components/AppShell';
import RedirectPreservingSearch from './components/RedirectPreservingSearch';
import LoginPanel from './components/LoginPanel';
import HomePage from './pages/HomePage';
import CreateGroupPage from './pages/CreateGroupPage';
import NewMigrationWizard from './pages/NewMigrationWizard';
import EventLogPage from './pages/EventLogPage';
import GroupLayout from './pages/GroupLayout';
import GroupsListPage from './pages/GroupsListPage';
import MigrationDevicesPage from './pages/MigrationDevicesPage';
import MigrationIdentitiesPage from './pages/MigrationIdentitiesPage';
import MigrationActivatePage from './pages/MigrationActivatePage';
import MigrationLayout from './pages/MigrationLayout';
import MigrationOverviewPage from './pages/MigrationOverviewPage';
import MigrationTenantsPage from './pages/MigrationTenantsPage';
import MigrationsListPage from './pages/MigrationsListPage';
import TenantLayout from './pages/TenantLayout';
import SettingsPage from './pages/SettingsPage';
import TenantsListPage from './pages/TenantsListPage';
import ConnectTenantPage from './pages/ConnectTenantPage';
import TenantConnectCallbackPage from './pages/TenantConnectCallbackPage';
import TenantConsentedPage from './pages/TenantConsentedPage';
import TenantOverviewPage from './pages/tenant/TenantOverviewPage';
import TenantServiceAccountsPage from './pages/tenant/TenantServiceAccountsPage';
import TenantApplicationPage from './pages/tenant/TenantApplicationPage';
import TenantServicePrincipalsPage from './pages/tenant/TenantServicePrincipalsPage';
import TenantMigrationsPage from './pages/tenant/TenantMigrationsPage';
import DeviceDetailPage from './pages/DeviceDetailPage';
import DevicesListPage from './pages/DevicesListPage';
import IdentitiesListPage from './pages/IdentitiesListPage';
import GroupMigrationsPage from './pages/group/GroupMigrationsPage';
import GroupOverviewPage from './pages/group/GroupOverviewPage';
import GroupSettingsPage from './pages/group/GroupSettingsPage';




function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div className="max-w-xl">
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Page not found</MessageBarTitle>
          <span className="text-xs font-medium">{location.pathname}</span> is not a page in
          this console.
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="subtle" size="small" onClick={() => navigate('/home')}>
            Back to Home
          </Button>
        </MessageBarActions>
      </MessageBar>
    </div>
  );
}


export default function App() {
  return (
    <>
      <AuthenticatedTemplate>
        <Routes>
          <Route element={<AppShell />}>
            {/* FD-466 — the root lands on Home, the AirBridge command centre,
                rather than on the campaigns list. `/migrations` keeps its own
                address: FD-ADR-043's scope boundary keeps existing URLs working,
                and the rail labels that route "Campaigns" at the render boundary
                (FD-ADR-031 §3) rather than renaming it. */}
            <Route path="/" element={<RedirectPreservingSearch to="/home" />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/migrations" element={<MigrationsListPage />} />
            <Route path="/migrations/new" element={<NewMigrationWizard />} />
            {/* FD-493 (R-FD-051 D3) — the wave-activation preflight, DECLARED
                BEFORE the `:migrationId` layout route it shares a prefix with.

                Same discipline as `/groups/new` above `/groups/:groupId`: a
                route that a `:param`-shaped sibling could swallow is declared
                first, and a route test asserts the resolution rather than
                trusting the ranker. It is a SIBLING rather than a fifth tab
                because it is a one-way approach to an irreversible decision —
                see the page's own header for why the tab row and the context
                column's Activate button must not be on screen beside it. */}
            <Route path="/migrations/:migrationId/activate" element={<MigrationActivatePage />} />
            <Route path="/migrations/:migrationId" element={<MigrationLayout />}>
              <Route index element={<RedirectPreservingSearch to="overview" />} />
              <Route path="overview" element={<MigrationOverviewPage />} />
              <Route path="devices" element={<MigrationDevicesPage />} />
              <Route path="identities" element={<MigrationIdentitiesPage />} />
              <Route path="tenants" element={<MigrationTenantsPage />} />
            </Route>
            <Route path="/migrations/:migrationId/events" element={<EventLogPage />} />
            {/* FD-283 — the device detail page is reachable from BOTH contexts.
                The campaign-scoped route exists so drilling in from a campaign
                keeps that context and its back link returns there; arriving from
                a campaign and landing back in the global list on Back is the
                disorientation this avoids. Same component, two routes. */}
            <Route
              path="/migrations/:migrationId/devices/:serialNumber"
              element={<DeviceDetailPage />}
            />
            <Route path="/devices" element={<DevicesListPage />} />
            <Route path="/devices/:serialNumber" element={<DeviceDetailPage />} />
            {/* FD-263 — `/groups/new` MUST be declared before `/groups/:groupId`,
                or the create route is swallowed by the param route with
                groupId === "new". Same ordering as `/migrations/new`. */}
            <Route path="/groups" element={<GroupsListPage />} />
            <Route path="/groups/new" element={<CreateGroupPage />} />
            <Route path="/groups/:groupId" element={<GroupLayout />}>
              <Route index element={<RedirectPreservingSearch to="overview" />} />
              <Route path="overview" element={<GroupOverviewPage />} />
              <Route path="migrations" element={<GroupMigrationsPage />} />
              <Route path="settings" element={<GroupSettingsPage />} />
            </Route>
            <Route path="/identities" element={<IdentitiesListPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/tenants" element={<TenantsListPage />} />
            {/* R-FD-055 — `/tenants/connect` MUST be declared before
                `/tenants/:tenantId`, or it is swallowed by the param route with
                tenantId === "connect". Same rule as `/groups/new`. */}
            <Route path="/tenants/connect" element={<ConnectTenantPage />} />
            {/* R-FD-055 — the two Microsoft returns. Literal paths, declared before
                `/tenants/:tenantId` for the same reason as `/tenants/connect`. */}
            <Route path="/tenants/connect/callback" element={<TenantConnectCallbackPage />} />
            <Route path="/tenants/consented" element={<TenantConsentedPage />} />
            <Route path="/tenants/:tenantId" element={<TenantLayout />}>
              <Route index element={<RedirectPreservingSearch to="overview" />} />
              <Route path="overview" element={<TenantOverviewPage />} />
              <Route path="application" element={<TenantApplicationPage />} />
              <Route path="service-accounts" element={<TenantServiceAccountsPage />} />
              <Route path="service-principals" element={<TenantServicePrincipalsPage />} />
              <Route path="migrations" element={<TenantMigrationsPage />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPanel />
      </UnauthenticatedTemplate>
    </>
  );
}
